import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import c from 'classnames';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useRequestPDFReport from 'services/pdfReport/useRequestPDFReport';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useTinyAccountNewUpdateState } from 'hooks/useNewUpdateIndicator';
import { capitalize, stoolToGut, parseDate } from 'helpers';
import {
  getKitById,
  getKitsForTinyAccountId,
  getLatestKitForTinyAccountId,
} from 'store/kits';
import { getTinyAccounts } from 'store/account';
import { Kit, KitType } from '@repo/types';

import { Badge, NewUpdateDot } from 'components';
import { Dropdown } from 'components/Elements';
import { Icon, Typography } from '@repo/ui';
import {
  kitStatusColorParser,
  kitStatusParser,
  sortKitsByResultsViewableOrLatest,
} from '@repo/utils';

import styles from './KitSelect.module.css';
import getKitLabel from '@repo/utils/getKitLabel';

interface BaseKitSelectProps {
  placement: 'left' | 'right';
}

const BaseKitSelect = ({ placement }: BaseKitSelectProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { currentKit, currentKitType, currentKitId, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  const kits = useSelector(getKitsForTinyAccountId(currentTinyAccountId)); // only show kits for this tiny account
  const latestKitId =
    useSelector(getLatestKitForTinyAccountId(currentTinyAccountId ?? null))
      ?.id ?? null;
  const { anyNonLatestKitHasNewUpdate } = useTinyAccountNewUpdateState(
    currentTinyAccountId ?? null,
    latestKitId,
  );

  const sortedKits = sortKitsByResultsViewableOrLatest(kits);
  const currentKitTypeIsStool = currentKitType === KitType.STOOL;
  const tinyAccounts = useSelector(getTinyAccounts);

  const setCurrentKit = (kitId: string) => {
    if (!kitId) return;
    const newPath = pathname.replace(`/${currentKitId}`, `/${kitId}`);
    navigate(newPath, { replace: true });
  };
  if (!kits || !currentKit) return null;

  if (!tinyAccounts?.length || !kits.length) return null;
  const kitSelectOptions = sortedKits.map((kit: Kit) => ({
    value: kit.id,
  }));

  const controlLabel = getKitLabel(currentKit);

  return (
    <div className={styles.kitSelectWrapper}>
      <NewUpdateDot
        show={anyNonLatestKitHasNewUpdate}
        className={styles.kitSelectCollapsedDot}
        ariaLabel='New update on another kit'
      />
      <Dropdown
        options={kitSelectOptions}
        value={currentKit.id}
        onChange={(value: string) => setCurrentKit(value)}
        placeholder={null}
        CustomOption={({ innerProps, ...props }) => (
          <KitSelectOption {...innerProps} {...props} />
        )}
        controlIconString={currentKitTypeIsStool ? 'swab' : 'lotus'}
        controlLabel={controlLabel}
        controlSubLabel={format(parseDate(currentKit.sample_date), 'MM/dd/yy')}
        hideControlLabelMobile
        placement={placement}
        isSearchable={false}
        // menuIsOpen
      />
    </div>
  );
};

export const KitSelectV1 = () => <BaseKitSelect placement='left' />;
export const KitSelectV2 = () => <BaseKitSelect placement='right' />;

const KitSelectOption = ({ ...props }) => {
  // option values are kitIds
  const kit = useSelector(getKitById(props.value));
  const tinyAccountId = kit?.tinyaccount_id ?? null;
  const latestKitId =
    useSelector(getLatestKitForTinyAccountId(tinyAccountId))?.id ?? null;
  const { perKit } = useTinyAccountNewUpdateState(tinyAccountId, latestKitId);
  if (!kit) return null;

  const kitLabel = getKitLabel(kit);
  const optionHasNewUpdate = !!(kit.id && perKit[kit.id]?.results);

  return (
    <div className={styles.kitSelectOption}>
      <div className={styles.iconLabelContainer}>
        <div className={styles.iconContainer}>
          <Icon
            size='m'
            name={kit.sampling_type === 'stool' ? 'swab' : 'lotus'}
          />
        </div>

        <div>
          <div className={styles.kitLabel}>{kitLabel}</div>
          {kit.sample_date && (
            <div className={styles.kitSubLabel}>
              {format(parseDate(kit.sample_date), 'MM/dd/yy')}
            </div>
          )}
        </div>
        <NewUpdateDot
          show={optionHasNewUpdate}
          className={styles.kitSelectOptionDot}
        />
      </div>

      <div className={styles.sampleLabel}>
        <Typography variant='label'>{kit.sample_label}</Typography>
      </div>

      <Badge
        title={kitStatusParser(kit.status)}
        state={kitStatusColorParser(kit.status)}
      />
    </div>
  );
};

export const SelectedKit = () => {
  const { currentKit, currentKitId, currentKitHasResults } =
    useCurrentKitAndTinyAccount();
  const { signedUrl } = useRequestPDFReport(currentKitId);
  const { pathname } = useLocation();
  const { trackEvent } = useAnalytics();

  const viewPDFReport = useCallback(async () => {
    if (signedUrl) {
      trackEvent('PDF Downloaded');
      window.open(signedUrl, '_blank');
    }
  }, [signedUrl, trackEvent]);

  if (!currentKit) return null;

  return (
    <div className={styles.selectedKitContainer}>
      <div className={c(styles.hideOnMobile, styles.kitInfo)}>
        <div className={styles.itemWrapper}>
          <Icon name='priceTagFilled' size='s' />
          <Typography variant='label'>{`Kit ${currentKit.id}`}</Typography>
        </div>
        <div className={styles.itemWrapper}>
          <Icon name='atSignFilled' size='s' />
          <Typography variant='label'>{currentKit.sample_label}</Typography>
        </div>
      </div>
      <div className={styles.kitInfo}>
        <div className={c(styles.itemWrapper, styles.hideOnDesktop)}>
          <Icon name='priceTagFilled' size='s' />
          <Typography variant='label'>{`Kit ${currentKit.id}`}</Typography>
        </div>
        {currentKit.date_survey_completed && currentKitHasResults && (
          <>
            <div
              className={c(
                styles.itemWrapper,
                styles.hideOnMobile,
                'clickable',
              )}
              onClick={viewPDFReport}
            >
              <Icon name='download' size='s' />
              <Typography variant='label'>Download PDF</Typography>
            </div>
            <div
              className={c(
                styles.itemWrapper,
                styles.hideOnDesktop,
                'clickable',
              )}
              onClick={viewPDFReport}
            >
              <Icon name='download' size='s' />
              <Typography variant='label'>PDF</Typography>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
